import { useEffect, useState } from 'react';
import API, { PUBLIC, UrlRedirectionResponseType } from '../lib/api';
import GlobalStore from '../app/global-store';
import { useAccountContext, AccountContextType } from '../lib/context-providers/account-context';

type UrlRedirectionState = {
  isChecking: boolean;
};

const isSamePassport = (redirectUrl: string, pathPrefix: string): boolean => {
  const target = new URL(redirectUrl);

  if (target.origin !== window.location.origin) {
    return false;
  }

  return !pathPrefix || target.pathname === pathPrefix || target.pathname.startsWith(`${pathPrefix}/`);
};

/**
 * Checks whether the current passport (resolved subdomain) should be redirected.
 * Redirects as soon as possible, any failure or disabled redirection resolves to normal app flow.
 */
export default function useUrlRedirection(): UrlRedirectionState {
  const { isAccountSubdomain } = useAccountContext() as AccountContextType;
  const passportSubdomain = GlobalStore.getInstance().eventSubdomain;
  const [ isChecking, setIsChecking ] = useState<boolean>(!!passportSubdomain);

  useEffect(() => {
    if (!passportSubdomain) {
      setIsChecking(false);
      return;
    }

    let isRedirecting = false;

    API[PUBLIC].fetchUrlRedirection(passportSubdomain)
      .then(({ urlRedirectionEnabled, redirectUrl }: UrlRedirectionResponseType) => {
        const pathPrefix = isAccountSubdomain ? `/${passportSubdomain}` : '';

        if (urlRedirectionEnabled && redirectUrl && !isSamePassport(redirectUrl, pathPrefix)) {
          isRedirecting = true;
          window.location.replace(redirectUrl);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        // keep rendering nothing while the browser navigates away to avoid flashing the UI
        if (!isRedirecting) {
          setIsChecking(false);
        }
      });
  }, [passportSubdomain, isAccountSubdomain]);

  return { isChecking };
}
